import React, { useState, useEffect, useCallback, memo } from "react";
import { useSelector } from "react-redux";
//components
import { Column, Row } from "components/Layout/Grid";
import QuoteMultiSelect from "../QuoteMultiSelect";
import { Visibility } from "components/Visibility";
import DateDashboard from "../DateDashboard";
import { Button } from "components/Button";
import DateFilter from "../DateFilter";
import Text from "components/Text";
//services
import QuoteBionexoMappingServices from "services/quoteBionexoMapping.service";
import KeycloakPubServiceInstance from "services/keycloakpubsub.service";
//interfaces
import { IOption } from "components/Select/interface";
import { IUser } from "models/User";
//context
import { useFiltersHeaderContext } from "contexts/FiltersHeader";
//utils
import { IsNullOrEmpty } from "utils/StringNullOrEmpty";
//assets
import "pages/Quotes/styles.scss";
import "./styles.scss";

interface FilterString {
  seller?: string[];
  category?: string[];
  subsidiary?: string[];
}

interface QuoteFilter {
  hideButtonFilter?: boolean;
  updateFilter: (filter: FilterString) => void;
}

const QuotationFilters: React.FC<QuoteFilter> = (props) => {
  const { updateFilter, hideButtonFilter } = props;
  const bcgOnly = process.env.REACT_APP_BCG_ONLY === 'true';

  const {
    selectedSeller,
    selectedCategories,
    selectedSubsidiary,
    dateFilterON,
    setDateFilterON,
  } = useFiltersHeaderContext();

  const [openOptions, setOpenOptions] = useState<boolean>(false);
  const [showCategories, setShowCategories] = useState<boolean>(false);
  const [showSubsidiary, setShowSubsidiary] = useState<boolean>(false);
  const [showRepresentatives, setShowRepresentatives] = useState<boolean>(false);
  const [categoriesOptions, setCategoriesOptions] = useState(Array<IOption>());
  const [subsidiaryOptions, setSubsidiaryOptions] = useState(Array<IOption>());
  const [sellersOptions, setSellersOptions] = useState(Array<IOption>());
  const [subsidiaryFilter, setSubsidiaryFilter] = useState(selectedSubsidiary ?? []);
  const [sellerFilter, setSellerFilter] = useState(selectedSeller ?? []);
  const [categoryFilter, setCategoryFilter] = useState(selectedCategories ?? []);

  const user = useSelector((state: { user: IUser; }) => state.user);

  const _quoteBionexoMappingServices = new QuoteBionexoMappingServices();

  const getSubsidiariesList = useCallback(async () => {
    KeycloakPubServiceInstance.subscribe(async () => {
      const result = await new QuoteBionexoMappingServices().getMoneyBusinessUnits();
      const _optionsSubsidiaries: Array<IOption> = new Array<IOption>();

      if (result.data) {
        const { data } = result;

        Object.keys(data)?.map((key) => {
          _optionsSubsidiaries.push({
            value: data[key].identifier,
            label: `${data[key].identifier} (${data[key].name.trim()})`
          });
        });
      }
      setSubsidiaryOptions(_optionsSubsidiaries);
    });
  }, []);

  useEffect(() => {
    KeycloakPubServiceInstance.subscribe(async () => {
      let _optionsCategories: Array<IOption> = new Array<IOption>();

      const result = await _quoteBionexoMappingServices.getCategories();
      const { data } = result;

      data && Object.keys(data).forEach((key) => {
        _optionsCategories = [..._optionsCategories, {
          value: data[key].categoryId,
          label: data[key].name
        }];
      });

      setCategoriesOptions(_optionsCategories);
    });
  }, []);

  const getSellersList = (branchCode: string[]) => {
    KeycloakPubServiceInstance.subscribe(async () => {
      setOpenOptions(true);

      const result = await new QuoteBionexoMappingServices().getSellerByBusinessUnit(branchCode);

      let _optionsSellers: Array<IOption> = new Array<IOption>();

      if (result.data) {
        const { data } = result;

        data && Object.keys(data).forEach((key) => {
          _optionsSellers = [..._optionsSellers, {
            value: data[key].code,
            label: `${data[key].code} - ${data[key].name}`
          }];
        });
      }

      setSellersOptions(_optionsSellers);
    });
  };

  const handleSellersFilter = () => {
    const isBCGUser = !IsNullOrEmpty(user?.buBCG);
    const applyBcgLogic = !bcgOnly && isBCGUser;
    const filterParam: string[] = applyBcgLogic ? user.buBCG?.split(",") || [] : subsidiaryFilter || [];

    if (!applyBcgLogic && (subsidiaryFilter === undefined || subsidiaryFilter.length === 0)) {
      setOpenOptions(false);
      setSellersOptions([]);
    } else {
      getSellersList(filterParam);
    }

    if (selectedSubsidiary !== subsidiaryFilter) {
      setSellerFilter([]);
    }
  };

  const mergeFilters = () => {
    const filterItems = {
      seller: sellerFilter,
      subsidiary: subsidiaryFilter,
      category: categoryFilter
    };

    updateFilter(filterItems);
  };

  useEffect(() => {
    if (hideButtonFilter)
      mergeFilters();
  }, [sellerFilter, subsidiaryFilter, categoryFilter]);

  useEffect(() => {
    handleSellersFilter();
  }, [subsidiaryFilter, user?.buBCG]);

  useEffect(() => {
    getSubsidiariesList();
  }, []);

  const sellerFilterTitle = sellerFilter?.length > 0 ? `Representantes selecionados (${sellerFilter?.length})` : "Todos os representantes";
  const categoryFilterTitle = categoryFilter?.length > 0 ? `Categorias selecionadas (${categoryFilter?.length})` : "Filtro de Categorias";
  const subsidiaryFilterTitleBCG = `BUS selecionadas (${user?.buBCG?.split(",").length})`;

  const getSubsidiaryFilterTitle = () => {
    const subsidiaryFilterTitle = subsidiaryFilter?.length > 0 ? `BUS selecionadas (${subsidiaryFilter?.length})` : "Filtro de BUS";
    
    if (bcgOnly || IsNullOrEmpty(user?.buBCG)) {
      return subsidiaryFilterTitle;
    } else {
      return subsidiaryFilterTitleBCG;
    }
  };
  
  const subsidiaryFilterTitle = getSubsidiaryFilterTitle();

  return (
    <Row className="mr-1 mr-md-0 flex-md-wrap flex-sm-wrap filters">
      <Column className="w-sm-100 w-sm-30 mr-md-3">
        <QuoteMultiSelect
          optionsToSelect={subsidiaryOptions}
          arraySelectedOptions={subsidiaryFilter}
          updateSelectedOptions={setSubsidiaryFilter}
          title={"BU"}
          placeholder={subsidiaryFilterTitle}
          show={showSubsidiary}
          setShow={setShowSubsidiary}
          className="w-sm-100 w-md-100 bu-input"
          style={{ minWidth: 190, height: "auto", backgroundColor: "white" }}
          openOptions={bcgOnly || IsNullOrEmpty(user?.buBCG)}
        />
      </Column>
      <Column className="ml-2 ml-sm-0 w-sm-100 w-30 mr-md-4">
        <QuoteMultiSelect
          optionsToSelect={sellersOptions}
          arraySelectedOptions={sellerFilter}
          updateSelectedOptions={setSellerFilter}
          title={"Representante"}
          placeholder={sellerFilterTitle}
          show={showRepresentatives}
          setShow={setShowRepresentatives}
          className="w-sm-100 w-md-100 seller-input"
          style={{ minWidth: 185, height: "auto", backgroundColor: "white" }}
          openOptions={openOptions}
          subsidiaryFilter={subsidiaryFilter}
        />
      </Column>
      <Column className="ml-2 ml-sm-0 w-sm-100 w-40">
        <QuoteMultiSelect
          optionsToSelect={categoriesOptions}
          arraySelectedOptions={categoryFilter}
          updateSelectedOptions={setCategoryFilter}
          title={"Categorias"}
          placeholder={categoryFilterTitle}
          show={showCategories}
          setShow={setShowCategories}
          className="w-sm-100 w-md-100 category-input"
          style={{ minWidth: 190, height: "auto", backgroundColor: "white" }}
        />
      </Column>
      <Visibility visible={!hideButtonFilter}>
        <Button
          className={`button bg-cyanLightViveo w-sm-70 w-md-40 ml-4 mt-3 w-40 filter-button`}
          height={44}
          width={92}
          onClick={() => { mergeFilters(); }}
        >
          <Text
            size={14}
            className="button-text color-primary font-bold"
          >
            Filtrar
          </Text>
        </Button>
      </Visibility>

      <Column className="mt-3 d-flex align-center flex-sm-wrap date-dashboard">

        <DateDashboard setDateFilterON={setDateFilterON} />
      </Column>
      {dateFilterON && <DateFilter setDateFilterON={setDateFilterON} />}
    </Row>
  );
};

export default memo(QuotationFilters);
